<template>
  <v-dialog v-model="dialog" persistent max-width="520px">
    <v-card class="expense-dialog-card">
      <div class="dialog-header">
        <div>
          <div class="dialog-title">Edit Expense</div>
          <div class="dialog-subtitle">Update expense details.</div>
        </div>

        <v-btn icon small @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </div>

      <v-card-text class="pa-4">
        <v-row dense>
          <v-col cols="12">
            <div class="field-label">Date</div>

            <DateSelector
              v-if="dialog"
              :initValue="Expense.date"
              v-model="Expense.date"
            />
          </v-col>

          <v-col cols="12">
            <div class="field-label">Payee</div>

            <v-autocomplete
              outlined
              dense
              auto-select-first
              :loading="loadingPayee"
              :items="Payee"
              label="Select Payee"
              item-text="name"
              item-value="id"
              :hide-details="selectPayeeError.length === 0"
              :error-messages="selectPayeeError"
              v-model="Expense.PayeeId"
            />
          </v-col>

          <v-col cols="12" sm="6">
            <div class="field-label">Amount</div>

            <v-text-field
              outlined
              dense
              prefix="$"
              type="number"
              min="0"
              step="0.01"
              @focus="$event.target.select()"
              class="right-input"
              v-model="Expense.amount"
              label="Amount"
              :hide-details="amountErrors.length === 0"
              :error-messages="amountErrors"
            />
          </v-col>

          <v-col cols="12">
            <div class="field-label">Description</div>

            <v-textarea
              @focus="$event.target.select()"
              outlined
              dense
              auto-grow
              rows="3"
              hide-details
              v-model="Expense.note"
              :label="$t('labels.description')"
            />
          </v-col>
        </v-row>
      </v-card-text>

      <v-divider />

      <v-card-actions class="dialog-actions">
        <v-spacer />

        <v-btn text color="primary" @click="close">
          Close
        </v-btn>

        <v-btn
          color="primary"
          depressed
          rounded
          :loading="loading"
          dark
          @click="submit"
        >
          Update Expense
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import expenseService from "../service.js";
import payeeService from "../Payee/service.js";
import DateSelector from "../../../components/DateSelector";

const { validationMixin } = require("vuelidate");
const { required, minValue } = require("vuelidate/lib/validators");

export default {
  name: "update-expense",

  props: {
    editDialog: {
      default: false,
    },
    editExpense: {
      type: Object,
      default: null,
    },
  },

  components: {
    DateSelector,
  },

  mixins: [validationMixin],

  data() {
    return {
      loading: false,
      loadingPayee: false,
      dialog: false,
      Payee: [],
      Expense: this.getEmptyExpense(),
    };
  },

  validations: {
    Expense: {
      PayeeId: {
        required,
        minValue: minValue(1),
      },
      amount: {
        required,
        minValue: minValue(0.01),
      },
    },
  },

  computed: {
    selectPayeeError() {
      const errors = [];

      if (!this.$v.Expense.PayeeId.$dirty) {
        return errors;
      }

      if (!this.$v.Expense.PayeeId.required) {
        errors.push(this.$t("validations.fieldIsRequired"));
      }

      if (!this.$v.Expense.PayeeId.minValue) {
        errors.push("Please select payee");
      }

      return errors;
    },

    amountErrors() {
      const errors = [];

      if (!this.$v.Expense.amount.$dirty) {
        return errors;
      }

      if (!this.$v.Expense.amount.required) {
        errors.push(this.$t("validations.fieldIsRequired"));
      }

      if (!this.$v.Expense.amount.minValue) {
        errors.push("Amount must be greater than 0");
      }

      return errors;
    },
  },

  watch: {
    editDialog(val) {
      this.dialog = val;

      if (val) {
        this.Expense = this.editExpense
          ? JSON.parse(JSON.stringify(this.editExpense))
          : this.getEmptyExpense();

        this.$v.$reset();
        this.getPayee();
      }
    },
  },

  methods: {
    getEmptyExpense() {
      return {
        id: null,
        date: this.$moment().toISOString(),
        PayeeId: null,
        amount: null,
        note: null,
      };
    },

    close() {
      this.empty();
      this.dialog = false;
      this.$emit("close");
    },

    empty() {
      this.Expense = this.getEmptyExpense();
      this.$v.$reset();
    },

    async getPayee() {
      this.loadingPayee = true;

      try {
        const response = await payeeService.getAll();
        this.Payee = response.data || [];
        return response;
      } catch (error) {
        console.error("get payee failed", error);
        this.Payee = [];
      } finally {
        this.loadingPayee = false;
      }
    },

    submit() {
      this.$v.$touch();

      if (this.$v.$invalid) {
        this.$swal
          .mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          })
          .fire({
            icon: "error",
            title: this.$t("toastMessage.text.invalidFields"),
          });

        return;
      }

      if (!this.Expense.id) {
        this.$swal
          .mixin({
            toast: true,
            position: "top-end",
            showConfirmButton: false,
            timer: 3000,
          })
          .fire({
            icon: "error",
            title: "Expense ID is missing",
          });

        return;
      }

      this.loading = true;

      const payload = {
        ...this.Expense,
        amount: Number(this.Expense.amount || 0),
      };

      return expenseService
        .update(this.Expense.id, payload)
        .then((result) => {
          if (result.status) {
            this.$swal
              .mixin({
                toast: true,
                position: "top-end",
                showConfirmButton: false,
                timer: 3000,
              })
              .fire({
                icon: "success",
                title: "Expense is updated",
              });

            this.$events.emit("expenseListEvent");
            this.$emit("submit");
            this.close();
          }
        })
        .catch((err) => {
          const message =
            err && err.data && err.data.message
              ? err.data.message
              : "Unable to update expense";

          this.$swal
            .mixin({
              toast: true,
              position: "top-end",
              showConfirmButton: false,
              timer: 3000,
            })
            .fire({
              icon: "error",
              title: message,
            });
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>

<style scoped>
.expense-dialog-card {
  border-radius: 20px;
  overflow: hidden;
}

.dialog-header {
  padding: 18px 20px;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.dialog-title {
  font-size: 20px;
  font-weight: 800;
  color: #0f172a;
}

.dialog-subtitle {
  font-size: 13px;
  color: #64748b;
  margin-top: 2px;
}

.field-label {
  font-size: 12px;
  font-weight: 700;
  color: #475569;
  margin-bottom: 6px;
}

.dialog-actions {
  padding: 12px 16px;
}

.right-input input {
  text-align: right;
}
</style>